import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type OtpContactKindT = "EMAIL" | "SMS" | "WSP";

export type RecoveryContactabilityT = {
  contactabilityId: string;
  contactType: "email" | "phone";
  kind: OtpContactKindT;
  masked: string;
  preferred?: boolean;
  disabled?: boolean;
};

type RecoveryStateT = {
  rut?: number;
  isResultOpen: boolean;
  isOtpInProgressOpen: boolean;
  useNewRecoveryFlow?: boolean;
  accountData: {
    email: string;
    phone: string;
  };
  selectedContactabilityId?: string;
  contactKind?: OtpContactKindT;
  maskedDestination?: string;
  otpDuration?: number;
  otpExpiresAt?: number;
};

type RecoveryActionsT = {
  setRut: (rut: RecoveryStateT["rut"]) => void;
  setResultOpen: (isOpen: boolean) => void;
  setOtpInProgressOpen: (isOpen: boolean) => void;
  setUseNewRecoveryFlow: (useNewRecoveryFlow: RecoveryStateT["useNewRecoveryFlow"]) => void;
  setAccountData: (accountData: RecoveryStateT["accountData"]) => void;
  setSelectedContactability: (params: {
    contactabilityId: string;
    contactKind: OtpContactKindT;
    maskedDestination: string;
  }) => void;
  setOtpDuration: (otpDuration: number | undefined) => void;
  setOtpExpiresAt: (otpExpiresAt: number | undefined) => void;
  resetOtp: () => void;
};

export type RecoveryStoreT = RecoveryStateT & RecoveryActionsT;

export const useRecoveryStore = create<RecoveryStoreT>()((set) => ({
  accountData: { email: "", phone: "" },
  isResultOpen: false,
  isOtpInProgressOpen: false,
  useNewRecoveryFlow: undefined,
  selectedContactabilityId: undefined,
  contactKind: undefined,
  maskedDestination: undefined,
  otpDuration: undefined,
  otpExpiresAt: undefined,
  setRut: (rut) => set({ rut }),
  setResultOpen: (isResultOpen) => set({ isResultOpen }),
  setOtpInProgressOpen: (isOtpInProgressOpen) => set({ isOtpInProgressOpen }),
  setUseNewRecoveryFlow: (useNewRecoveryFlow) => set({ useNewRecoveryFlow }),
  setAccountData: (accountData) => set({ accountData }),
  setSelectedContactability: ({ contactabilityId, contactKind, maskedDestination }) =>
    set({ selectedContactabilityId: contactabilityId, contactKind, maskedDestination }),
  setOtpDuration: (otpDuration) => set({ otpDuration }),
  setOtpExpiresAt: (otpExpiresAt) => set({ otpExpiresAt }),
  resetOtp: () =>
    set({
      selectedContactabilityId: undefined,
      contactKind: undefined,
      maskedDestination: undefined,
      otpDuration: undefined,
      otpExpiresAt: undefined,
    }),
}));

type PersistingRecoveryStoreT = {
  rut: number;
  setRut: (rut: number) => void;
  transactionId: string;
  isHydrated: boolean;
  isOtpDataUpdate: boolean;
  setTransactionId: (id: string) => void;
  setHydrated: (isHydrated: boolean) => void;
  setIsOtpDataUpdate: (isOtpDataUpdate: boolean) => void;
};

export const usePersistingRecoveryStore = create(
  persist<PersistingRecoveryStoreT>(
    (set) => ({
      rut: 0,
      setRut: (rut: number) => set({ rut }),
      transactionId: "",
      isHydrated: false,
      isOtpDataUpdate: false,
      setTransactionId: (transactionId: string) => set({ transactionId }),
      setHydrated: (isHydrated: boolean) => set({ isHydrated }),
      setIsOtpDataUpdate: (isOtpDataUpdate: boolean) => set({ isOtpDataUpdate }),
    }),
    {
      name: "account-recovery-storage",
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        state?.setTransactionId(state.transactionId);
        state?.setHydrated(true);
      },
    }
  )
);
